import { z } from 'zod';

/**
 * Standard error envelope · per ADR-001 §Error envelope.
 * `code` = camelCase semantic identifier (`invalidEmailFormat` · `sessionExpired` · `migrationConflict`).
 * `message` = human-readable RU Phase 3 (i18n Phase 5 per OQ-5).
 * `details` = optional structured info for validation errors / sync conflict resolution payload.
 */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

/**
 * Pagination envelope · per ADR-001 §Pagination.
 * Used on `/api/quiz/attempts` · `/api/practice/all` · future paginated routes.
 * `/api/progress` is NOT paginated (always-current full state).
 */
export const PaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  size: z.number().int().min(1).max(100),
  total: z.number().int().min(0),
});

export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce.number().int().min(1).max(100).default(20),
});

export function paginatedResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    pagination: PaginationMetaSchema,
  });
}

/**
 * Sync conflict body shape · returned with HTTP 412 · per ADR-007.
 * Client uses to refetch + retry (max 1 retry · second 412 surfaces error).
 */
export const SyncConflictDetailsSchema = z.object({
  currentBranch: z.unknown(),
  currentETag: z.string(),
  branchPath: z.string(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;
export type SyncConflictDetails = z.infer<typeof SyncConflictDetailsSchema>;
